import React from 'react';
import {
  ISubFieldVisibility,
  IExternalBusinessDetail
} from '@Shared.Angular/@types/core/contracts/queryModel/externalBusinessProvider/externalBusinessProvider';
import { EXTERNAL_BUSINESS_FIELD_LABELS } from '@Shared.Angular/@types/core/contracts/constants/externalBusinessFieldLabels';

interface IBusinessDisplayField {
  key: string;
  label: string;
  value: string;
  isHidden: boolean;
}

type IBusinessDisplayProps = {
  data: string;
  visibility?: ISubFieldVisibility;
};

const parseBusinessData = (
  text: string | null | undefined,
  visibility?: ISubFieldVisibility
): {
  businessData: Partial<IExternalBusinessDetail> | null;
  fields: IBusinessDisplayField[];
} => {
  if (!text) {
    return { businessData: null, fields: [] };
  }

  try {
    const parsed: Partial<IExternalBusinessDetail> = JSON.parse(text);
    const isManualEntry = parsed.isManualEntry;

    const fields = Object.entries(EXTERNAL_BUSINESS_FIELD_LABELS)
      .map(([key, label]) => ({
        key,
        label,
        value: parsed[key as keyof IExternalBusinessDetail]?.toString() || '',
        isHidden:
          visibility && visibility[key as keyof ISubFieldVisibility] === false
      }))
      .filter(({ value }) => {
        // For manual entry, show all visible fields and all hidden fields (even if empty)
        if (isManualEntry) {
          return true;
        }
        // For lookup, only show fields with values
        return value !== '';
      });

    return { businessData: parsed, fields };
  } catch (error) {
    console.error('Failed to parse business data:', error);
    return { businessData: null, fields: [] };
  }
};

export const BusinessDisplay: React.FC<IBusinessDisplayProps> = ({
  data,
  visibility
}) => {
  const { businessData, fields } = React.useMemo(() => {
    return parseBusinessData(data, visibility);
  }, [data, visibility]);

  if (!businessData || fields.length === 0) {
    return null;
  }

  const shownFields = fields.filter((f) => !f.isHidden);
  const hiddenFields = fields.filter((f) => f.isHidden);
  const isManualEntry = businessData.isManualEntry;

  return (
    <div className="business-display">
      {/* Manual Entry Indicator */}
      {isManualEntry && (
        <div className="business-display__manual-entry">
          <input
            type="checkbox"
            className="filled-in"
            id="business-display-manual-entry"
            checked={true}
            disabled={true}
          />
          <label htmlFor="business-display-manual-entry">
            Can't find my company. Enter details manually.
          </label>
        </div>
      )}

      {/* Visible Fields */}
      {shownFields.length > 0 && (
        <div className="business-display__content">
          {shownFields.map(({ key, label, value }) => (
            <div key={key} className="business-display__row">
              <span className="business-display__label">{label}:</span>
              <span className="business-display__value">
                {isManualEntry && !value ? 'Unavailable' : value}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Hidden Fields Section */}
      {hiddenFields.length > 0 && (
        <div className="business-display__hidden-section">
          <div className="business-display__hidden-header">Hidden Fields</div>
          <div className="business-display__content">
            {hiddenFields.map(({ key, label, value }) => (
              <div key={key} className="business-display__row">
                <span className="business-display__label">{label}:</span>
                <span className="business-display__value">
                  {isManualEntry && !value ? 'Unavailable' : value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
